import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import AddIcon from '@mui/icons-material/esm/Add';
import EventIcon from '@mui/icons-material/esm/Event';
import EventBusyIcon from '@mui/icons-material/esm/EventBusy';
import EventAvailableIcon from '@mui/icons-material/esm/EventAvailable';
import DeleteIcon from '@mui/icons-material/esm/Delete';

import { PageHeader } from '../../components/layout';
import { KPICard } from '../../components/common';
import calendarService, {
  CalendarListItem,
  CalendarDetail,
  CalendarDay,
  DAY_BIT,
  DAY_LABEL,
  ExceptionType,
} from '../../services/api/calendarService';

const DAY_ORDER: CalendarDay[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const EXCEPTION_LABEL: Record<ExceptionType, string> = {
  holiday: 'Holiday',
  blackout: 'Blackout',
  working: 'Working',
};

const EXCEPTION_COLOR: Record<ExceptionType, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  holiday: 'info',
  blackout: 'error',
  working: 'success',
};

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function CalendarsAdminPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [calendars, setCalendars] = useState<CalendarListItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<CalendarDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createProjectId, setCreateProjectId] = useState('');
  const [createDays, setCreateDays] = useState<CalendarDay[]>(['mon', 'tue', 'wed', 'thu', 'fri']);

  // Exception form
  const [exceptionDate, setExceptionDate] = useState(todayIso());
  const [exceptionType, setExceptionType] = useState<ExceptionType>('holiday');
  const [exceptionDescription, setExceptionDescription] = useState('');

  const loadCalendars = async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await calendarService.list();
      setCalendars(list);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load calendars');
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id: string | null) => {
    if (!id) {
      setDetail(null);
      return;
    }
    try {
      setDetailLoading(true);
      setDetailError(null);
      const d = await calendarService.get(id);
      setDetail(d);
    } catch (err: any) {
      setDetailError(err.response?.data?.message || 'Failed to load calendar detail');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadCalendars();
  }, []);

  useEffect(() => {
    loadDetail(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const summary = useMemo(() => {
    const total = calendars.length;
    const globalActive = calendars.find((c) => c.scope === 'global' && c.isActive);
    const projectActive = calendars.filter((c) => c.scope === 'project' && c.isActive).length;
    const totalExceptions = calendars.reduce((sum, c) => sum + c.exceptionCount, 0);
    return { total, globalActive, projectActive, totalExceptions };
  }, [calendars]);

  const handleCreate = async () => {
    if (!createName.trim()) return;
    try {
      setSaving(true);
      setError(null);
      const cal = await calendarService.create({
        name: createName.trim(),
        projectId: createProjectId.trim() || null,
        workingDaysMask: createDays.reduce((sum, d) => sum + DAY_BIT[d], 0),
        isActive: true,
      });
      setCreateOpen(false);
      setCreateName('');
      setCreateProjectId('');
      setCreateDays(['mon', 'tue', 'wed', 'thu', 'fri']);
      await loadCalendars();
      setSelectedId(cal.id);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create calendar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDay = async (day: CalendarDay) => {
    if (!detail) return;
    const has = (detail.workingDaysMask & DAY_BIT[day]) === DAY_BIT[day];
    const newMask = has
      ? detail.workingDaysMask & ~DAY_BIT[day]
      : detail.workingDaysMask | DAY_BIT[day];
    try {
      setSaving(true);
      await calendarService.update(detail.id, { workingDaysMask: newMask });
      await loadCalendars();
      await loadDetail(detail.id);
    } catch (err: any) {
      setDetailError(err.response?.data?.message || 'Failed to update working days');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!detail) return;
    try {
      setSaving(true);
      await calendarService.update(detail.id, { isActive: !detail.isActive });
      await loadCalendars();
      await loadDetail(detail.id);
    } catch (err: any) {
      setDetailError(err.response?.data?.message || 'Failed to toggle active');
    } finally {
      setSaving(false);
    }
  };

  const handleAddException = async () => {
    if (!detail || !exceptionDate) return;
    try {
      setSaving(true);
      await calendarService.addException(detail.id, {
        exceptionDate,
        exceptionType,
        description: exceptionDescription.trim() || undefined,
      });
      setExceptionDate(todayIso());
      setExceptionDescription('');
      await loadDetail(detail.id);
      await loadCalendars();
    } catch (err: any) {
      setDetailError(err.response?.data?.message || 'Failed to add exception');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveException = async (id: string) => {
    if (!detail) return;
    try {
      setSaving(true);
      await calendarService.removeException(id);
      await loadDetail(detail.id);
      await loadCalendars();
    } catch (err: any) {
      setDetailError(err.response?.data?.message || 'Failed to remove exception');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCalendar = async (id: string) => {
    try {
      setSaving(true);
      await calendarService.remove(id);
      if (selectedId === id) setSelectedId(null);
      await loadCalendars();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete calendar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Work Calendars"
        subtitle="Working-day patterns + holiday/blackout exceptions used by the readiness engine."
        actions={
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setCreateOpen(true)}>
            New calendar
          </Button>
        }
      />

      {/* Summary KPIs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Calendars" value={`${summary.total}`} icon={<EventIcon />} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Global default"
            value={summary.globalActive ? summary.globalActive.name : 'None'}
            changeLabel={summary.globalActive ? summary.globalActive.workingDays.map((d) => DAY_LABEL[d]).join(' · ') : 'Set one to enable calendar-aware readiness'}
            icon={<EventAvailableIcon />}
            color={summary.globalActive ? 'success' : 'warning'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Per-project overrides"
            value={`${summary.projectActive}`}
            changeLabel="Active project-scoped calendars"
            icon={<EventIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total exceptions"
            value={`${summary.totalExceptions}`}
            changeLabel="Holidays + blackouts + working overrides"
            icon={<EventBusyIcon />}
            color="info"
          />
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2}>
        {/* Left: calendar list */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Calendars</Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : calendars.length === 0 ? (
              <Alert severity="info">No calendars yet. Create one to enable calendar-aware readiness.</Alert>
            ) : (
              <Stack spacing={1}>
                {calendars.map((cal) => (
                  <Paper
                    key={cal.id}
                    variant={selectedId === cal.id ? 'elevation' : 'outlined'}
                    elevation={selectedId === cal.id ? 4 : 0}
                    sx={{ p: 1.5, cursor: 'pointer', borderColor: selectedId === cal.id ? 'primary.main' : undefined }}
                    onClick={() => setSelectedId(cal.id)}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" noWrap>{cal.name}</Typography>
                        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip size="small" label={cal.scope === 'global' ? 'Global' : cal.projectName ?? 'Project'} variant="outlined" />
                          {cal.isActive ? (
                            <Chip size="small" label="Active" color="success" />
                          ) : (
                            <Chip size="small" label="Inactive" />
                          )}
                          {cal.exceptionCount > 0 && (
                            <Chip size="small" label={`${cal.exceptionCount} exc.`} variant="outlined" />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {cal.workingDays.map((d) => DAY_LABEL[d]).join(' · ') || 'No working days'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Right: detail */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, minHeight: 400 }}>
            {!selectedId ? (
              <Alert severity="info">Select a calendar to view and edit its working days and exceptions.</Alert>
            ) : detailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : detail ? (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">{detail.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {detail.scope === 'global' ? 'Global default' : detail.projectName} · updated {new Date(detail.updatedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <FormControlLabel
                      control={<Switch checked={detail.isActive} onChange={handleToggleActive} disabled={saving} />}
                      label={detail.isActive ? 'Active' : 'Inactive'}
                    />
                    <Tooltip title="Delete calendar (admin only — cannot undo)">
                      <span>
                        <IconButton color="error" onClick={() => handleDeleteCalendar(detail.id)} disabled={saving}>
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>

                {detailError && <Alert severity="error" sx={{ mb: 2 }}>{detailError}</Alert>}

                <Typography variant="subtitle2" gutterBottom>Working days</Typography>
                <ToggleButtonGroup
                  size="small"
                  sx={{ flexWrap: 'wrap' }}
                  value={DAY_ORDER.filter((d) => (detail.workingDaysMask & DAY_BIT[d]) === DAY_BIT[d])}
                >
                  {DAY_ORDER.map((day) => (
                    <ToggleButton
                      key={day}
                      value={day}
                      disabled={saving}
                      onClick={() => handleToggleDay(day)}
                    >
                      {DAY_LABEL[day]}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle2" gutterBottom>Add exception</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2, alignItems: 'flex-start' }}>
                  <TextField
                    size="small"
                    type="date"
                    label="Date"
                    value={exceptionDate}
                    onChange={(e) => setExceptionDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 160 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Type</InputLabel>
                    <Select value={exceptionType} label="Type" onChange={(e) => setExceptionType(e.target.value as ExceptionType)}>
                      <MenuItem value="holiday">Holiday (closed)</MenuItem>
                      <MenuItem value="blackout">Blackout (closed)</MenuItem>
                      <MenuItem value="working">Working (open)</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Description (optional)"
                    value={exceptionDescription}
                    onChange={(e) => setExceptionDescription(e.target.value)}
                    sx={{ flexGrow: 1, minWidth: 200 }}
                  />
                  <Button variant="contained" onClick={handleAddException} disabled={saving || !exceptionDate}>
                    Add
                  </Button>
                </Stack>

                <Typography variant="subtitle2" gutterBottom>Exceptions ({detail.exceptions.length})</Typography>
                {detail.exceptions.length === 0 ? (
                  <Alert severity="info">No exceptions in the current year. Add holidays + planned blackouts here.</Alert>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.exceptions.map((ex) => (
                        <TableRow key={ex.id}>
                          <TableCell>{ex.exceptionDate}</TableCell>
                          <TableCell>
                            <Chip size="small" label={EXCEPTION_LABEL[ex.exceptionType]} color={EXCEPTION_COLOR[ex.exceptionType]} />
                          </TableCell>
                          <TableCell>{ex.description ?? '—'}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Remove exception">
                              <IconButton size="small" onClick={() => handleRemoveException(ex.id)} disabled={saving}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </>
            ) : (
              <Alert severity="error">Calendar not found.</Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New work calendar</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              size="small"
              label="Calendar name"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              autoFocus
              fullWidth
            />
            <TextField
              size="small"
              label="Project ID (leave blank for global default)"
              value={createProjectId}
              onChange={(e) => setCreateProjectId(e.target.value)}
              helperText="Per-project calendar takes precedence over the global default for that project's tasks."
              fullWidth
            />
            <Box>
              <Typography variant="caption" color="text.secondary">Working days</Typography>
              <ToggleButtonGroup
                size="small"
                sx={{ display: 'flex', flexWrap: 'wrap', mt: 0.5 }}
                value={createDays}
                onChange={(_, value) => setCreateDays(value as CalendarDay[])}
              >
                {DAY_ORDER.map((day) => (
                  <ToggleButton key={day} value={day}>{DAY_LABEL[day]}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Alert severity="info">
              Creating an active calendar will deactivate any other active calendar in the same scope.
            </Alert>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
              <Button variant="contained" onClick={handleCreate} disabled={saving || !createName.trim()}>
                Create
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
